import { useState } from 'react';
import { addMinutes } from 'date-fns';
import { useCountdown } from 'src/hooks/useCountdown';
import type { GetNewCodeConfig } from 'src/components/AuthNew/types';
import { api } from 'src/api';
import type { PostClientSendOtpSmsResponse } from '@lt/api/dist/methods/client/types';
import type { AxiosError } from 'axios';
import { useRecaptchaController } from 'src/components/ReCaptchaModal';

const FALLBACK_DELIVER_DELAY_MINUTES = 1;

export type UseGetNewCodeReturn = ReturnType<typeof useGetNewCode>;

export const useGetNewCode = () => {
  // отвечает за управление капчей
  const { isOpenReCaptcha, handleRecaptchaRequired, captchaCallback } =
    useRecaptchaController();
  const [canBeDeliveredAt, setCanBeDeliveredAt] = useState<Date>(
    () => new Date(),
  );

  const { count: timeLeftToDeliver, timer } = useCountdown(
    canBeDeliveredAt,
    1000,
  );

  const unixTimestampToDate = (time?: number) => {
    if (!time) return addMinutes(new Date(), FALLBACK_DELIVER_DELAY_MINUTES);
    return new Date(time * 1000);
  };

  const startTimer = (deliverTime?: number) => {
    setCanBeDeliveredAt(unixTimestampToDate(deliverTime));
  };

  async function handleGetNewCode({
    phone,
    client_id,
    onSuccess,
    onError,
    event,
    captchaCallbackSuccess,
  }: GetNewCodeConfig): Promise<
    PostClientSendOtpSmsResponse | { error: AxiosError; success: false }
  > {
    try {
      if (!phone) {
        throw new Error(`phone is not define: ${phone}`);
      }
      const response = await api.postClientSendOtpSms({
        phone,
        client_id,
        event,
      });
      const { success } = response;

      if (success) {
        onSuccess?.();
      }

      if (!success && response?.code === 'captcha_required') {
        handleRecaptchaRequired(async () => {
          const res = await handleGetNewCode({
            phone,
            client_id,
            onSuccess,
            onError,
            event,
          });
          if (res.success) {
            captchaCallbackSuccess?.();
            startTimer(res.can_be_delivered_at);
          }
        });
      }

      startTimer();
      return response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const deliverAt = error?.response?.data?.can_be_delivered_at;

      startTimer(deliverAt);
      onError?.(error);

      return { success: false, error };
    }
  }

  return {
    timeLeftToDeliver,
    handleGetNewCode,
    timer,
    startTimer,
    isOpenReCaptcha,
    captchaCallback,
  };
};
